import { Anchor } from "@svea/design-system-components-react";
import { Heading, Paper, Stack, useText, Text } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import Content from "../../components/content";
import Hide from "../../components/hide";
import { withLoader } from "../../hoc/with-loader";
import { usePageContext } from "../../hooks/context";
import { usePackage } from "../../hooks/package";
import { usePages } from "../../hooks/usePages";
import type { BankGiroFormValues } from "../../types/bankgiro";
import type { ChoosePackageError } from "../../types/package";
import { isValidBgForm } from "../../utils/validation";
import BankgiroHeader from "./bankgiro-header";
import BankgiroSettings from "./bankgiro-settings";
import ChooseAddons from "./choose-addons";
import ChoosePackageFooter from "./choose-package-footer";
import { CreditAlert } from "./credit-alert";
import CreditSummation from "./credit-summation";
import { useChoosePackage } from "./hooks";
import useOnboardingPackages from "./hooks/useOnboardingPackages";
import { usePromotedPackage } from "./hooks/usePromotedPackage";
import useSelectPackage from "./hooks/useSelectPackage";
import PromotedPackage from "./promoted-package";

const ChoosePackagePage = () => {
	const { steps, creditScore, bankGiroFormValues } = usePageContext(useChoosePackage);
	const { hasCheckCreditProduct, selectedPackage } = usePackage();
	const [t, texts] = useText();
	const { nextPage } = usePages();
	const navigate = useNavigate();
	const [error, setError] = useState<ChoosePackageError | undefined>();

	useSelectPackage();

	const promotedPackage = usePromotedPackage();
	const onboardingPackages = useOnboardingPackages();

	const isBankgiroSettingsAvailable = selectedPackage?.packageId !== "FORETAGARNA-CARDONLY";
	const hasAddons = selectedPackage?.addons && selectedPackage.addons.length > 0;

	useEffect(() => {
		setError(undefined);
	}, [steps]);

	const validateSteps = (bgValues?: BankGiroFormValues) => {
		const hasCompletedPackageStep = steps?.find(
			(s) => s.name === "SelectPackageStep" && s.status === "Completed",
		);
		if (!hasCompletedPackageStep) {
			setError({
				id: 1,
				message: t(texts.validation.packageNotSelected),
			});
			return false;
		}
		if (!isValidBgForm(bgValues, hasCheckCreditProduct)) {
			setError({ id: 3, message: t(texts.validation.invalidBg) });
			return false;
		}
		return true;
	};

	const onContinue = () => {
		if (!selectedPackage) {
			setError({
				id: 1,
				message: t(texts.validation.packageNotSelected),
			});

			return;
		}

		sessionStorage.removeItem("preSelectedPackage");

		if (!isBankgiroSettingsAvailable) {
			if (!nextPage) throw new Error("Tried navigating to nextPage without nextPage available");
			navigate(nextPage);
		}
	};

	return (
		<>
			<CreditSummation creditScore={creditScore} />

			<Content my={8}>
				<Stack gap={12}>
					<Hide from="tablet">
						<Anchor
							as={Link}
							to={t(texts.choosePackage.externalLink.link)}
							target="_blank"
							iconEnd="chevronRight"
						>
							{t(texts.choosePackage.externalLink.description)}
						</Anchor>
					</Hide>

					<Stack gap={6}>
						<Stack align="center" direction="row">
							<Text className="text-text-headline text-2xl font-bold">
								{t(texts.choosePackage.stepTitle)}
							</Text>
						</Stack>

						<CreditAlert />

						{promotedPackage?.packageId && (
							<div className="pb-6">
								<Stack align="center" gap={12}>
									<PromotedPackage />
									<Hide from="tablet">
										<div className="pb-2">
											<Heading.H3>{t(texts.choosePackage.otherPackagesTitle)}</Heading.H3>
										</div>
									</Hide>
								</Stack>
							</div>
						)}

						<div className="tablet:grid-cols-2 tablet:gap-6 grid w-full grid-cols-1 gap-8">
							{onboardingPackages.map(([packageId, component]) => {
								return <div key={packageId}>{component}</div>;
							})}
						</div>
					</Stack>

					{selectedPackage && (
						<>
							{hasAddons && (
								<Stack gap={6}>
									<Text className="text-text-headline text-2xl font-bold">
										{t(texts.chooseAddons.title)}
									</Text>

									<Paper
										plain
										className="bg-background-complementary-gray max-md:-mx-4 max-md:rounded-none max-md:border-none"
										padding="base"
									>
										<ChooseAddons />
									</Paper>
								</Stack>
							)}

							{isBankgiroSettingsAvailable && (
								<Stack gap={6}>
									<BankgiroHeader />

									<Paper plain padding="base">
										<BankgiroSettings
											bankGiroFormValues={bankGiroFormValues}
											validateSteps={validateSteps}
										/>
									</Paper>
								</Stack>
							)}
						</>
					)}

					<ChoosePackageFooter error={error} onContinue={onContinue} />
				</Stack>
			</Content>
		</>
	);
};

export default withLoader(useChoosePackage, ChoosePackagePage);
